/**
 * client/src/crm/CrmErrorBoundary.tsx
 *
 * WHY THIS FILE EXISTS — read before deleting it as boilerplate.
 * =============================================================
 * This SPA rewrites every unmatched path to `index.html`. That is what makes
 * client-side routing work, and it has a consequence people keep rediscovering
 * the hard way: **the server can no longer tell you that something is broken.**
 *
 *   - A URL that matches no route does not 404. It serves `index.html`, React
 *     mounts, matches nothing, and renders an empty `<div id="root">`.
 *   - An uncaught exception during render does the same thing for a different
 *     reason: since React 16, an error thrown in the render phase with no error
 *     boundary above it UNMOUNTS THE ENTIRE TREE. Not the broken subtree — the
 *     whole app. The user gets a white page, HTTP 200, and no message.
 *
 * Both failure modes look identical from the outside: a blank page with a 200.
 * This application has a documented history of exactly that failure, which is
 * why the seam edit in `client/src/App.tsx` puts the CRM route inside a
 * boundary rather than trusting the module not to throw.
 *
 * A `<Suspense>` fallback does NOT cover this. Suspense handles a pending lazy
 * import; it does nothing about a render that throws. A rejected `React.lazy()`
 * import — the offline-user, stale-hash, bad-deploy case — throws during render
 * and needs this boundary to be caught. So the CRM island needs both, and this
 * component must sit OUTSIDE the Suspense boundary to catch a chunk that fails
 * to load at all.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 *  - It does not catch errors in event handlers, `setTimeout`, or async code.
 *    React error boundaries never have; that is a React limitation, not an
 *    oversight here. Data-fetch failures are handled explicitly as `ApiError`
 *    state in each screen, which is the right place for them anyway.
 *  - It does not retry automatically. A render that threw once will usually
 *    throw again with the same props, and an automatic retry turns a visible
 *    bug into an invisible render loop. The user gets a button.
 *  - It does not swallow the error. It always `console.error`s it, so the stack
 *    is in the console and in whatever error reporter is wired to it, even
 *    though the user sees a calm message.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

export interface CrmErrorBoundaryProps {
  children: ReactNode;
  /** Shown above the message. Defaults to a neutral CRM-specific line. */
  title?: string;
  /**
   * Optional reporter (Sentry, a logging endpoint, a test spy). Called in
   * addition to `console.error`, never instead of it.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * Change this value to clear a caught error — pass the current pathname so
   * navigating away from a broken screen recovers instead of stranding the user
   * on the fallback for the rest of the session.
   */
  resetKey?: string;
}

interface CrmErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

export class CrmErrorBoundary extends Component<CrmErrorBoundaryProps, CrmErrorBoundaryState> {
  constructor(props: CrmErrorBoundaryProps) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  /** Render-phase: swap in the fallback. No side effects allowed here. */
  static getDerivedStateFromError(error: Error): Partial<CrmErrorBoundaryState> {
    return { error };
  }

  /**
   * Commit-phase: side effects are allowed, so this is where the error is
   * reported. `info.componentStack` names the component that threw, which is
   * the single most useful line when the symptom is "the page went white".
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // Always logged, even when a reporter is attached. A user-visible fallback
    // that leaves nothing in the console is how a bug survives three releases.
    // eslint-disable-next-line no-console
    console.error('[crm] uncaught render error', error, info.componentStack);
    if (this.props.onError) {
      try {
        this.props.onError(error, info);
      } catch {
        // A throwing reporter must not replace the original error with its own.
      }
    }
  }

  /** Clear the error when the caller's `resetKey` changes (e.g. on navigation). */
  componentDidUpdate(prev: CrmErrorBoundaryProps): void {
    if (this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: null });
    }
  }

  private handleRetry = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (error === null) {
      return this.props.children;
    }

    const title = this.props.title ?? 'The CRM screen could not be displayed';

    return (
      <div className="vb-crm vb-crm-boundary" role="alert">
        <div className="vb-crm-boundary-card">
          <h2 className="vb-crm-boundary-title">{title}</h2>
          <p className="vb-crm-boundary-lede">
            Something in this screen failed while rendering. Nothing was saved or changed by
            the failure, and the rest of the application is unaffected.
          </p>
          {/*
            The message is shown, not hidden behind "an error occurred". A rep who
            can read "Failed to fetch dynamically imported module" can tell support
            something useful; a rep looking at a shrug cannot.
          */}
          <p className="vb-crm-boundary-message">
            <code>{error.message || String(error)}</code>
          </p>
          <div className="vb-crm-boundary-actions">
            <button type="button" className="vb-crm-btn" onClick={this.handleRetry}>
              Try this screen again
            </button>
            <a className="vb-crm-btn vb-crm-btn-quiet" href="/">
              Back to the dashboard
            </a>
          </div>
          {componentStack ? (
            <details className="vb-crm-boundary-details">
              <summary>Technical detail</summary>
              <pre className="vb-crm-boundary-stack">{componentStack}</pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}

export default CrmErrorBoundary;
